import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageShell from './LegalPageShell';
import { LEGAL_CONTROLLER, LEGAL_RETENTION } from './legalConfig';

/**
 * GDPR Art. 13/14–oriented Privacy Policy (draft for DPO/counsel review).
 */
export default function PrivacyPolicy() {
  const c = LEGAL_CONTROLLER;
  const r = LEGAL_RETENTION;

  return (
    <LegalPageShell title="Privacy Policy" path="/privacy">
      <p>
        This Privacy Policy describes how <strong>{c.productName}</strong> (“CyberTracer”, “we”,
        “us”) processes personal data when you use the OSINT due diligence workspace at{' '}
        <strong>{c.siteUrl}</strong>. We design processing to align with the EU General Data
        Protection Regulation (GDPR / Regulation (EU) 2016/679), Romanian Law no. 190/2018, and —
        for cookies / terminal storage — Romanian Law no. 506/2004.
      </p>

      <h2>1. Controller</h2>
      <ul>
        <li>
          <strong>Legal name:</strong> {c.legalName}
        </li>
        <li>
          <strong>Trade / product:</strong> {c.tradeName}
        </li>
        <li>
          <strong>CUI / registration:</strong> {c.cui}
        </li>
        <li>
          <strong>Registered address:</strong> {c.registeredAddress}
        </li>
        <li>
          <strong>Privacy contact:</strong>{' '}
          <a href={`mailto:${c.email}`}>{c.email}</a>
        </li>
        <li>
          <strong>DPO / privacy desk (draft):</strong>{' '}
          <a href={`mailto:${c.dpoEmail}`}>{c.dpoEmail}</a>
        </li>
      </ul>

      <h2>2. Roles and intended use</h2>
      <p>
        CyberTracer is an OSINT workspace for <strong>professional</strong> due diligence, B2B
        anti-fraud, cyber risk, and sanctions awareness. Depending on the engagement, CyberTracer
        may act as <strong>controller</strong> for website / analytics / admin operations, and as{' '}
        <strong>processor</strong> or <strong>joint controller</strong> for investigation queries
        run by a business customer. Counsel should confirm the contractual model (DPA / joint
        controller arrangement) for each B2B deployment.
      </p>
      <p>
        Users must have a lawful mandate to investigate. Searching a natural person without a valid
        legal basis remains the responsibility of the user / customer organisation.
      </p>

      <h2>3. Categories of data we process</h2>
      <ul>
        <li>
          <strong>Account / access (admin):</strong> Google OAuth identity for authorised operators
          (email, subject id) when SSO is enabled.
        </li>
        <li>
          <strong>Investigation inputs:</strong> names, emails, phone numbers, usernames, domains,
          free-text context you submit to run a scan.
        </li>
        <li>
          <strong>Investigation outputs:</strong> public OSINT metadata aggregated from third-party
          sources (search indexes, breach indexes, registries, public pages).
        </li>
        <li>
          <strong>Technical / security:</strong> IP addresses (hashed for admin analytics), user
          agent, request paths, error and access logs, container / process debug snapshots (HTOP).
        </li>
        <li>
          <strong>Cookie / consent evidence:</strong> your consent choice, timestamp, and policy
          version stored in the browser (see <Link to="/cookies">Cookie Policy</Link>).
        </li>
        <li>
          <strong>Optional analytics:</strong> Google Tag Manager / GA4 events only if you accept
          Analytics &amp; Ads.
        </li>
      </ul>

      <h2>4. What we do not intentionally store</h2>
      <ul>
        <li>Passwords, credential dumps, or illicit database corpora</li>
        <li>Private banking or financial account secrets</li>
        <li>Raw dark-web paste bodies or exfiltrated files</li>
      </ul>
      <p>
        Dark Web advisories are metadata-derived. We do not retrieve or retain unauthorized dump
        corpora.
      </p>

      <h2>5. Purposes and legal bases (draft)</h2>
      <p>Counsel must map each purpose to a definitive Art. 6 basis. Working draft:</p>
      <ul>
        <li>
          <strong>Provide the OSINT service you request</strong> — performance of a contract /
          steps at your request (Art. 6(1)(b)), and/or legitimate interests of the customer in
          professional risk assessment (Art. 6(1)(f)), subject to a documented LIA.
        </li>
        <li>
          <strong>Security, abuse prevention, admin operations</strong> — legitimate interests
          (Art. 6(1)(f)).
        </li>
        <li>
          <strong>Analytics &amp; advertising cookies</strong> — consent (Art. 6(1)(a) and Law
          506/2004 Art. 4(5)).
        </li>
        <li>
          <strong>Sanctions / PEP screening against curated public datasets</strong> — legitimate
          interests / legal obligations of the customer where applicable (confirm per use case).
        </li>
      </ul>

      <h2>6. Recipients and processors (illustrative)</h2>
      <ul>
        <li>Hosting / infrastructure providers operating the CyberTracer stack</li>
        <li>Search / enrichment APIs configured for the deployment (e.g. Serper, Brave)</li>
        <li>Google (Tag Manager / Analytics when consented; Google OAuth for admin SSO)</li>
        <li>Email / support channel for privacy requests</li>
      </ul>
      <p>
        A current processor list and SCCs / transfer tools for any non-EEA recipients should be
        attached by counsel.
      </p>

      <h2>7. International transfers</h2>
      <p>
        Some providers may process data outside the EEA. Where this occurs, we rely on appropriate
        safeguards (e.g. EU Standard Contractual Clauses) as confirmed by counsel for each vendor.
      </p>

      <h2>8. Retention</h2>
      <ul>
        <li>
          OSINT result cache (Redis): about <strong>{r.osintResultCacheHours} hours</strong> (configurable)
        </li>
        <li>
          DNS / WHOIS enrichment cache: about <strong>{r.dnsWhoisCacheDays} days</strong>
        </li>
        <li>
          Search preprocess cache: about <strong>{r.preprocessCacheHours} hours</strong>
        </li>
        <li>
          Admin search analytics (redacted queries + IP hash): up to{' '}
          <strong>{r.adminLastSearchesDays} days</strong> and capped list length
        </li>
        <li>
          HTOP debug snapshots: <strong>{r.adminHtopHours} hours</strong>
        </li>
        <li>
          Client search traces: about <strong>{r.clientSearchTraceHours} hours</strong>
        </li>
        <li>
          Nginx / reverse-proxy logs: {r.nginxAccessLogsDays}
        </li>
        <li>
          Browser consent record: {r.consentEvidenceLocal}
        </li>
      </ul>
      <p>
        Cache entries exist to reduce latency and API cost. They are <strong>not</strong> a
        permanent dossier. TTL alone does not replace your organisation&apos;s retention policy for
        exported PDF/CSV reports you download locally.
      </p>

      <h2>9. Storage clarification</h2>
      <p>
        CyberTracer <strong>does process and may temporarily cache</strong> investigation inputs and
        public OSINT outputs in memory and Redis to deliver the service. Admin analytics store{' '}
        <strong>redacted</strong> query fragments and hashed IP identifiers. We do not claim that
        “no personal data is ever processed or cached.”
      </p>

      <h2>10. Your rights</h2>
      <p>Subject to GDPR conditions, you may request:</p>
      <ul>
        <li>Access, rectification, erasure</li>
        <li>Restriction or objection to processing</li>
        <li>Data portability where applicable</li>
        <li>Withdrawal of consent for analytics cookies (without affecting prior lawful processing)</li>
      </ul>
      <p>
        Contact <a href={`mailto:${c.email}`}>{c.email}</a>. You may also lodge a complaint with{' '}
        <a href={c.supervisoryAuthority.url} target="_blank" rel="noopener noreferrer">
          {c.supervisoryAuthority.name}
        </a>
        .
      </p>

      <h2>11. Cookies</h2>
      <p>
        Details on necessary vs optional cookies, GTM, and how to change your choice are in the{' '}
        <Link to="/cookies">Cookie Policy</Link>.
      </p>

      <h2>12. Security measures (summary)</h2>
      <ul>
        <li>TLS on the public edge; admin SSO / token gates where configured</li>
        <li>PII redaction and IP hashing for admin analytics</li>
        <li>Subprocess allowlisting for OSINT CLIs; SSRF guards on outbound URL fetches</li>
        <li>Rate limiting and abuse controls on public API paths</li>
      </ul>

      <h2>13. Children</h2>
      <p>
        CyberTracer is not directed at children. Do not submit data about minors unless you have a
        clear lawful basis and necessity.
      </p>

      <h2>14. Changes</h2>
      <p>
        We may update this Policy. The version date above will change; material changes affecting
        cookies will prompt a new consent choice where required.
      </p>

      <h2>15. Contact</h2>
      <p>
        Privacy questions:{' '}
        <a href={`mailto:${c.email}`}>{c.email}</a>
      </p>
    </LegalPageShell>
  );
}
